import type { SessionUser } from '@golf/common';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from './api.ts';
import { MAGASIN_ETAT, effacer, lire, remplacer } from './local.ts';

interface AuthState {
    user: SessionUser | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
    isAdmin: boolean;
}

/** La dernière identité connue, gardée pour les démarrages sans réseau. */
const CLE_MOI = 'moi';

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<SessionUser | null>(null);
    const [loading, setLoading] = useState(true);

    const refreshUser = useCallback(async () => {
        try {
            const { user: u } = await api<{ user: SessionUser }>('/auth/me');
            setUser(u);
            void remplacer(MAGASIN_ETAT, u, CLE_MOI);
        } catch (e) {
            // Un serveur MUET n'est pas un refus : voir le commentaire du
            // démarrage ci-dessous. On garde la session telle qu'elle est.
            if (e instanceof ApiError) {
                setUser(null);
                void effacer(MAGASIN_ETAT, CLE_MOI);
            }
        }
    }, []);

    useEffect(() => {
        // Au premier chargement, /auth/me échoue si le jeton d'accès a expiré
        // pendant que l'application était fermée. On tente donc un
        // rafraîchissement avant de conclure que personne n'est connecté :
        // sans cela, rouvrir la PWA le lendemain renverrait à l'écran de
        // connexion alors que la session de 30 jours est encore valide.
        void (async () => {
            try {
                const { user: u } = await api<{ user: SessionUser }>('/auth/me');
                setUser(u);
                void remplacer(MAGASIN_ETAT, u, CLE_MOI);
            } catch (premier) {
                try {
                    if (!(premier instanceof ApiError)) throw premier;
                    await api('/auth/refresh', { method: 'POST' });
                    const { user: u } = await api<{ user: SessionUser }>('/auth/me');
                    setUser(u);
                    void remplacer(MAGASIN_ETAT, u, CLE_MOI);
                } catch (e) {
                    /* ── Un serveur MUET n'est pas un refus ──────────────────
                     *
                     * On rouvre l'application au septième trou, dans un creux
                     * sans réseau : « /auth/me » n'aboutit pas, et conclure de
                     * cet échec que personne n'est connecté renvoyait à l'écran
                     * de connexion — un formulaire que le joueur ne peut même
                     * pas valider, puisqu'il faudrait un serveur pour cela.
                     * Toute la carte en cours devenait inatteignable au moment
                     * précis où elle sert.
                     *
                     * On distingue donc les deux : une RÉPONSE du serveur qui
                     * refuse ferme la session ; une absence de réponse laisse
                     * la dernière identité connue en place. Rien n'est ouvert
                     * pour autant — chaque requête reste vérifiée à l'arrivée,
                     * et les écritures faites en chemin attendent dans la file
                     * qu'une vraie session les accepte. */
                    if (e instanceof ApiError) {
                        setUser(null);
                        void effacer(MAGASIN_ETAT, CLE_MOI);
                    } else {
                        setUser(await lire<SessionUser>(MAGASIN_ETAT, CLE_MOI));
                    }
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const login = useCallback(async (email: string, password: string) => {
        const { user: u } = await api<{ user: SessionUser }>('/auth/login', {
            method: 'POST',
            body: { email, password },
        });
        setUser(u);
    }, []);

    /* La déconnexion VOULUE efface l'identité gardée : c'est la différence
     *  avec une coupure, où on la conserve. */
    const logout = useCallback(async () => {
        void effacer(MAGASIN_ETAT, CLE_MOI);
        try {
            await api('/auth/logout', { method: 'POST' });
        } finally {
            setUser(null);
        }
    }, []);

    const value = useMemo<AuthState>(
        () => ({ user, loading, login, logout, refreshUser, isAdmin: user?.role === 'admin' }),
        [user, loading, login, logout, refreshUser],
    );

    return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth doit être utilisé dans un AuthProvider');
    return ctx;
}
